import {
  computed,
  defineComponent,
  inject,
  onMounted,
  onUnmounted,
  ref,
  Ref,
  RendererElement,
  RendererNode,
  VNode,
  watch,
  watchEffect
} from "vue";
import { useHandleAnalytics } from "../lib/analytics";
import { AnalyticEvents } from "@fresh8/types/src/analytics";
import { createUseStyles } from "@fresh8/vue-jss/src";
import debounce from "lodash.debounce";
import CarouselNavigation from "./CarouselNavigation.vue";
import { injectAdSize } from "../hooks/useAdSize";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import {
  useTemplateSize,
  SMALL_RESPONSIVE,
  LEADERBOARD,
  LARGE_LEADERBOARD,
  LARGE_RESPONSIVE,
  BILLBOARD,
  SKYSCRAPER,
  LARGE_MPU,
  MOBILE_LEADERBOARD
} from "@fresh8/ad-data-composition/src/useTemplateSize";
import * as smoothscroll from "smoothscroll-polyfill";
import { useHandleCardAnalytics } from "../lib/card-analytics";
import { TemplateSize } from "@fresh8/types/src";
import { isTouchDevice } from "../lib/isTouchDevice";
import { provideCarouselContext } from "../lib/provideInjectors";

// Hack to get around importing vue files into JSX
// eslint-disable-next-line
const _CarouselNavigation = CarouselNavigation as any;

const useStyles = createUseStyles({
  carousel: {
    display: "flex",
    flex: 1,
    position: "relative",

    width: "100%",
    minHeight: "0",
    WebkitOverflowScrolling: "touch"
  },
  navigation: {
    height: "100%",
    position: "absolute",
    flex: "0 0 30px",
    zIndex: 900,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 1,

    "&.smallSlider": {
      "& .nav-button": {
        transition: "all 0.1s ease",
        position: "relative",

        opacity: 0.5,
        "& > svg": {
          transition: "all 0.1s ease",
          position: "relative",
          left: "-1%"
        }
      },
      "&:hover": {
        "& .nav-button": {
          // left: "-2px",
          opacity: 1,
          "& > svg": {
            left: "-1%"
          }
        }
      }
    }
  },
  navNext: {
    justifyContent: "flex-end",
    right: "-10px",
    width: props => {
      if (props.isTouch && props.showMobileNav) {
        return props.nextHoverWidth ?? "30px";
      }

      return props.nextWidth ?? "30px";
    },
    transition: "all 150ms cubic-bezier(.3,.1,.3,1)",

    "&:hover": {
      opacity: 1,
      width: props => `${props.nextHoverWidth ?? props.nextWidth}px`
    }
  },
  navPrev: {
    left: props => (props.isMLB ? "0" : "-10px"),
    justifyContent: "flex-start",
    padding: "0",
    width: props => {
      if (props.isTouch && props.showMobileNav) {
        return props.previousHoverWidth;
      }

      return props.previousWidth ?? "30px";
    },
    transition: "all 150ms cubic-bezier(.3,.1,.3,1)",

    "&:hover": {
      opacity: 1,
      width: props => `${props.previousHoverWidth}px`
    }
  },
  slidesContainer: {
    flex: "1 0 100%",
    position: "relative",
    WebkitOverflowScrolling: "touch",
    overflowX: props =>
      props.noScroll || (props.isLongMLB && props.noOfItems === 2)
        ? "hidden"
        : "scroll",
    overflowY: "hidden",
    display: "flex",
    // gap: props => props.gap + "px",
    marginLeft: props => (props.extendCarouselToAdSide ? "-16px" : "0"),
    marginRight: props => (props.extendCarouselToAdSide ? "-16px" : "0"),
    scrollSnapType: props =>
      props.isLongMLB && props.noOfItems === 2 ? "" : "x mandatory",
    "-webkit-scroll-snap-type": props =>
      props.isLongMLB && props.noOfItems === 2 ? "" : "x mandatory",

    scrollbarWidth: "none",
    scrollBehavior: "smooth",

    "&::-webkit-scrollbar": {
      display: "none"
    },
    "&.ms-touch": {
      msOverflowStyle: "none",
      // Hides the scrollbar.
      msScrollChaining: "none"
    }
  },

  slide: {
    boxSizing: "border-box",
    flex: 1,
    width: "100%",
    scrollMarginLeft: props =>
      props.extendCarouselToAdSide ? "16px" : `${props.gap / 2}px`,
    scrollSnapMarginLeft: props =>
      props.extendCarouselToAdSide ? "16px" : `${props.gap / 2}px`,

    scrollSnapAlign: "start",
    display: "inline-block",

    height: "100%",
    position: "relative",
    marginLeft: props => props.gap + "px",

    "&:not(:last-of-type):after": {
      content: "''",
      display: props => (props.isLongMLB ? "initial" : "none"),
      position: "absolute",
      height: "100%",
      width: "1px",
      backgroundColor: props => props.separatorColor,
      opacity: "0.5",
      top: 0,
      right: props => (props.gap * -1) / 2
    },

    "&:first-of-type": {
      marginLeft: props => (props.extendCarouselToAdSide ? "16px" : "0")
    },

    "&:last-of-type": {
      marginRight: "16px"
    }
  }
});

export const getBreakpoints = (
  size: TemplateSize
): {
  gap: number;
  itemMaxWidth: number;
  offSet: number;
  previousWidth?: number;
  previousHoverWidth?: number;
  teaseWidth: number;
  nextWidth?: number;
  nextHoverWidth?: number;
} => {
  switch (size.name) {
    case SMALL_RESPONSIVE.name:
      return {
        gap: 6,
        itemMaxWidth: 380,
        offSet: 0,
        previousWidth: 18,
        previousHoverWidth: 38,
        teaseWidth: 56
      };
    case LEADERBOARD.name:
      return {
        gap: 4,
        itemMaxWidth: 428,
        offSet: 143,
        previousWidth: 16,
        previousHoverWidth: 30,
        teaseWidth: 162
      };
    case LARGE_LEADERBOARD.name:
      return {
        gap: 4,
        itemMaxWidth: 600,
        offSet: 144,
        previousWidth: 15,
        previousHoverWidth: 20,
        teaseWidth: 155
      };
    case LARGE_RESPONSIVE.name:
    case BILLBOARD.name:
      return {
        gap: 8,
        itemMaxWidth: 335,
        offSet: 0,
        previousWidth: 20,
        previousHoverWidth: 42,
        teaseWidth: 68
      };
    case SKYSCRAPER.name:
      return {
        gap: 8,
        itemMaxWidth: 235,
        offSet: 0,
        previousWidth: 12,
        previousHoverWidth: 20,
        teaseWidth: 12,
        nextWidth: 16
      };
    case LARGE_MPU.name:
      return {
        gap: 8,
        itemMaxWidth: 370,
        offSet: 0,
        previousWidth: 18,
        previousHoverWidth: 25,
        nextWidth: 32,
        teaseWidth: 40
      };

    case MOBILE_LEADERBOARD.name:
      return {
        gap: 8,
        itemMaxWidth: 307,
        offSet: 59,
        previousWidth: 15,
        nextWidth: 30,
        teaseWidth: 20
      };

    default:
      return {
        gap: 10,
        itemMaxWidth: 370,
        offSet: 0,
        previousWidth: 25,
        previousHoverWidth: 42,
        nextWidth: 25,
        nextHoverWidth: 30,
        teaseWidth: 50
      };
  }
};

enum ButtonStates {
  Start,
  Intermediate,
  End,
  Hide
}

smoothscroll.polyfill();
export default defineComponent({
  name: "TouchSlider",
  components: {},
  setup(props, context) {
    // to calculate the adsize -> for the arrows
    const { size } = useTemplateSize();
    const adSize = injectAdSize();
    const brandContext = injectBrand();

    const activeSlide = ref(0);

    const isTouch = isTouchDevice();

    const buttonState = ref<ButtonStates>(ButtonStates.Hide);

    /**
     * Updates the current button state, between start, end, and between both.
     */

    /** Width of the whole visible ad */
    const adWidth = computed(() => {
      return (size && size.value.width) || adSize.value.width;
    });

    /** Reference card sizes */
    const breakpointSizings = computed(() => {
      return getBreakpoints(size.value);
    });

    const nextSlideTeaseWidth = computed(() => {
      if (displayNavigation.value) {
        return 0;
      }

      return breakpointSizings.value.teaseWidth;
    });

    /** Amount of full cars visible in the ad */
    const itemsOnView = computed(() => {
      if (adWidth.value) {
        const result = Math.round(
          (adWidth.value -
            breakpointSizings.value.offSet -
            breakpointSizings.value.teaseWidth) /
            breakpointSizings.value.itemMaxWidth
        );

        return slotItems.value.length > result
          ? result
          : slotItems.value.length;
      }
      return 1;
    });

    const displayNavigation = computed(() => {
      return slotItems.value.length > itemsOnView.value ?? true;
    });

    const isMLB = computed(() => size.value.name === MOBILE_LEADERBOARD.name);

    /** Card Width */
    const itemWidth = computed(() => {
      const tease =
        itemsOnView.value >= slotItems.value.length
          ? 0
          : breakpointSizings.value.teaseWidth;

      const res =
        (adWidth.value -
          breakpointSizings.value.offSet -
          tease -
          breakpointSizings.value.gap * (itemsOnView.value + 1)) /
        itemsOnView.value;

      return res;
    });

    const separatorColor = brandContext?.getColorStyle(
      brandContext?.adTemplate.value?.marketBorderColor
    );

    const $handleAnalytics = useHandleAnalytics();
    const $handleCardAnalytics = useHandleCardAnalytics();
    const slotItems: Ref<VNode<
      RendererNode,
      RendererElement,
      { [key: string]: any }
    >[]> = ref([]);
    // Take each slot and merge their children together to make up the slides
    for (const slot of context?.slots?.default?.() || []) {
      if (slot.children != "v-if") {
        for (const child of slot.children as []) {
          slotItems.value.push(child);
        }
      }
    }
    const slider = ref<HTMLElement>();
    const slidesContainer = ref<HTMLElement>();
    const sliderWidth: Ref<number> = computed(() => {
      return adWidth.value - breakpointSizings.value.offSet;
    });

    watch(
      [activeSlide, itemsOnView],
      ([value, itemsOnView]) => {
        if (slotItems.value.length === itemsOnView) {
          buttonState.value = ButtonStates.Hide;
          return;
        }

        if (value === 0) {
          buttonState.value = ButtonStates.Start;
          return;
        }

        if (value === slotItems.value.length - itemsOnView) {
          buttonState.value = ButtonStates.End;
          return;
        }

        buttonState.value = ButtonStates.Intermediate;
      },
      { flush: "sync", immediate: true }
    );

    const yDown = ref(0);
    const xDown = ref(0);
    const initialDirection = ref<string>();

    const callAnalyticEvent = debounce(
      () => {
        const statsEvent = AnalyticEvents.INTERACTED;
        const statsDesc = isTouch
          ? "Carousel Mobile Swipe"
          : "Carousel Navigation";

        $handleAnalytics(statsEvent, statsDesc);
        $handleCardAnalytics(statsEvent, statsDesc);

        buttonClick.value = false;
      },
      500,
      { trailing: true }
    );

    const handleTouchStart = (evt: TouchEvent) => {
      buttonClick.value = false;
      const firstTouch = evt.touches[0];
      xDown.value = firstTouch.clientX;
      yDown.value = firstTouch.clientY;
    };

    const handleTouchMove = (evt: TouchEvent) => {
      buttonClick.value = false;
      if (initialDirection.value || !xDown.value || !yDown.value) {
        return;
      }

      const xUp = evt.touches[0].clientX;
      const yUp = evt.touches[0].clientY;

      const xDiff = (xDown.value || 0) - xUp;
      const yDiff = (yDown.value || 0) - yUp;

      // only call interaction if swipe is horizontal
      if (Math.abs(xDiff) > Math.abs(yDiff)) {
        initialDirection.value = "horizontal";
      } else {
        initialDirection.value = "vertical";
      }
    };

    const buttonClick = ref(false);

    const moveBackward = (event: MouseEvent) => {
      buttonClick.value = true;
      event.preventDefault();
      event.stopPropagation();
      slidesContainer.value?.scrollBy({
        behavior: "smooth",
        left: -itemWidth.value
      });

      activeSlide.value = activeSlide.value - 1;
    };

    const scrollingToStart = ref(false);

    const moveForward = (event: MouseEvent) => {
      buttonClick.value = true;

      // if currently scrolling to the start disable button clicks
      if (scrollingToStart.value) return;
      event.preventDefault();
      event.stopPropagation();

      if (activeSlide.value === slotItems.value.length - itemsOnView.value) {
        scrollingToStart.value = true;
        slidesContainer.value?.scrollTo({
          behavior: "smooth",
          top: 0,
          left: 0
        });

        setTimeout(() => {
          // need to timeout the scroll animation before setting the activeslide to zero and re enabling the button
          scrollingToStart.value = false;
          activeSlide.value = 0;
        }, 500);
        return;
      } else {
        if (scrollingToStart.value) return;
        slidesContainer.value?.scrollBy({
          behavior: "smooth",
          left: itemWidth.value
        });

        activeSlide.value += 1;
        return;
      }
    };

    /**
     * maintain position when resized and items on view changes
     */
    watch(itemsOnView, value => {
      slidesContainer.value?.scrollTo({
        behavior: "smooth",
        top: 0,
        left: activeSlide.value * itemWidth.value - value
      });
    });

    function handleScroll() {
      // ignore scroll event if it is caused by clicking on a nav button

      if (buttonClick.value) {
        callAnalyticEvent();

        return;
      }

      // only call interaction if swipe is horizontal

      if (isTouch && supportsScrollSnap.value) {
        if (initialDirection.value !== "horizontal") return;
      }

      if (!slidesContainer.value?.children?.length) return;

      // creating a datastructure from all cards where element index which is closest to zero will be the current active elements index
      const slidesRects = ([
        ...slidesContainer.value?.children
      ] as HTMLElement[]).map((e: HTMLElement, i, obj: HTMLElement[]) =>
        Math.abs(
          slidesContainer.value!.scrollLeft - (e.offsetLeft - obj[0].offsetLeft)
        )
      );

      debounce(
        () => {
          const currentSlide = Math.min(...slidesRects);

          const index = slidesRects.findIndex(v => v === currentSlide);

          if (activeSlide.value === index) return;

          activeSlide.value = index;
          callAnalyticEvent();
          initialDirection.value = undefined;
        },
        300,
        { leading: true }
      )();
    }

    onMounted(() => {
      if (isTouch) {
        slidesContainer.value?.classList.add("ms-touch");
      }
      slidesContainer.value?.addEventListener(
        "touchstart",
        handleTouchStart,
        false
      );
      slidesContainer.value?.addEventListener(
        "touchmove",
        handleTouchMove,
        false
      );

      slidesContainer.value?.addEventListener("scroll", handleScroll, false);
    });

    onUnmounted(() => {
      slidesContainer.value?.removeEventListener(
        "touchstart",
        handleTouchStart
      );

      slidesContainer.value?.removeEventListener("touchmove", handleTouchMove);

      slidesContainer.value?.removeEventListener("scroll", handleScroll);
    });

    const currentEventId = inject("currentEvent", {
      setCurrentEventId: (value: string) => void 0
    });

    /**
     * set current event id context to the event id of the current card (used in MLB header for displaying current event sport)
     */
    watch(
      activeSlide,
      newIndex => {
        currentEventId.setCurrentEventId(
          slotItems.value[newIndex]?.props?.eventId ?? ""
        );
      },
      { immediate: true }
    );

    const supportsScrollSnap = computed(() =>
      CSS.supports("scroll-snap-type: x mandatory")
    );

    const showPreviousNavButton = computed(() => {
      if (
        size.value.name === MOBILE_LEADERBOARD.name &&
        size.value.width !== 980 &&
        !brandContext?.brand.value?.showMobileNav
      ) {
        return false;
      }

      if (isTouch && !brandContext?.brand.value?.showMobileNav) {
        if (!supportsScrollSnap.value) return true;

        return false;
      }

      if (slotItems.value.length <= itemsOnView.value) return false;

      return buttonState.value !== ButtonStates.Start;
    });

    const showNextNavButton = computed(() => {
      if (
        size.value.name === MOBILE_LEADERBOARD.name &&
        size.value.width !== 980 &&
        !brandContext?.brand.value?.showMobileNav
      ) {
        return false;
      }

      if (isTouch && !brandContext?.brand.value?.showMobileNav) {
        if (!supportsScrollSnap.value) return true;

        return false;
      }

      if (buttonState.value == ButtonStates.Hide) return false;

      return true;
    });

    const classes = useStyles(
      ref({
        extendCarouselToAdSide: ![MOBILE_LEADERBOARD.name].includes(
          size.value.name
        ),
        noScroll: slotItems.value.length === itemsOnView.value,
        separatorColor,
        noOfItems: slotItems.value.length,
        isMLB: size.value.name === MOBILE_LEADERBOARD.name,
        isLongMLB:
          size.value.name === MOBILE_LEADERBOARD.name && size.value.width > 300,
        isTouch: isTouch,
        showMobileNav: brandContext?.brand.value?.showMobileNav,
        isMicro: size.value.name === SKYSCRAPER.name,
        ...breakpointSizings.value
      })
    );

    const carouselContext = ref({
      isCarousel: true,
      itemsOnView: itemsOnView.value
    });

    watch(itemsOnView, value => {
      carouselContext.value = {
        isCarousel: true,
        itemsOnView: value
      };
    });

    provideCarouselContext(carouselContext);

    const smallSlider = computed(
      () =>
        size.value.name === MOBILE_LEADERBOARD.name ||
        size.value.name === SMALL_RESPONSIVE.name ||
        size.value.name === LEADERBOARD.name ||
        size.value.name === LARGE_LEADERBOARD.name ||
        size.value.name === SKYSCRAPER.name
    );

    return () => (
      <div class={classes.value.carousel} ref={slider}>
        {showPreviousNavButton.value && (
          <div
            class={[
              classes.value.navigation,
              classes.value.navPrev,
              { smallSlider: smallSlider.value }
            ]}
            onClick={moveBackward}
          >
            <_CarouselNavigation
              direction="previous"
              class="nav-button previous"
            />
          </div>
        )}
        <div
          class={[classes.value.slidesContainer, "slidesContainer"]}
          style={{ width: `${sliderWidth.value}px` }}
          ref={slidesContainer}
        >
          {slotItems.value.map((item, index) => (
            <>
              <div
                class={[
                  classes.value.slide,
                  "slide",
                  `slide-type-${item?.props?.cardType}`
                ]}
                key={index}
                style={{
                  minWidth: `${itemWidth.value}px`,
                  maxWidth: `${itemWidth.value}px`
                }}
              >
                {item}
              </div>
            </>
          ))}
        </div>
        {showNextNavButton.value && (
          <div
            class={[
              classes.value.navigation,
              classes.value.navNext,
              { smallSlider: isMLB.value }
            ]}
            onClick={moveForward}
          >
            <_CarouselNavigation
              direction="next"
              returnButton={buttonState.value === 2}
              class="nav-button next"
            />
          </div>
        )}
      </div>
    );
  }
});
